// Client-side license photo downscaling.
//
// Why this exists: the submit request carries the license photo to the server,
// which then base64-encodes it and hands it to SendGrid as an attachment. A
// modern phone camera JPEG is 3-8 MB. On weak LTE (the real-world case that
// prompted this: one bar, truck stop) that upload alone can exceed the request
// budget, and the driver sees a "Submission Failed" that is really just the
// browser giving up mid-upload.
//
// Downscaling to MAX_EDGE at QUALITY typically cuts a phone photo by 80%+ while
// leaving a CDL entirely legible — the text on a license is large relative to
// the frame, so 1600px on the long edge is well past what's needed to read it.
//
// Fail-open by design: if anything here goes wrong (unsupported format, canvas
// blocked, decode error) we return the ORIGINAL image untouched. A large photo
// that uploads slowly is vastly better than a driver who cannot submit at all.

const MAX_EDGE = 1600;
const QUALITY = 0.8;

// Only downscale when it actually helps. Small images are passed through.
const MIN_BYTES_TO_COMPRESS = 600 * 1024; // 600 KB

/**
 * @param {File} file - the image File from the file input
 * @returns {Promise<{base64: string, name: string, compressed: boolean}>}
 *          base64 is the raw payload WITHOUT the data: URL prefix, matching
 *          what the rest of the app already expects for photoBase64.
 */
export function compressLicensePhoto(file) {
  return new Promise((resolve) => {
    const readOriginal = () => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        resolve({
          base64: String(ev.target.result).split(',')[1],
          name: file.name,
          compressed: false,
        });
      };
      reader.onerror = () => resolve({ base64: null, name: file.name, compressed: false });
      reader.readAsDataURL(file);
    };

    // PNGs of a license are rare and re-encoding them to JPEG changes the
    // filename/extension contract the backend uses. Only touch JPEGs.
    const isJpeg = file.type === 'image/jpeg' || file.type === 'image/jpg';
    if (!isJpeg || file.size < MIN_BYTES_TO_COMPRESS) {
      readOriginal();
      return;
    }

    let objectUrl = null;
    const cleanup = () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };

    try {
      const img = new Image();

      img.onload = () => {
        try {
          const { width, height } = img;
          if (!width || !height) { cleanup(); readOriginal(); return; }

          const longEdge = Math.max(width, height);
          const scale = longEdge > MAX_EDGE ? MAX_EDGE / longEdge : 1;

          // Already small enough in dimensions — re-encoding at QUALITY still
          // usually shrinks the file, so proceed rather than bail.
          const targetW = Math.round(width * scale);
          const targetH = Math.round(height * scale);

          const canvas = document.createElement('canvas');
          canvas.width = targetW;
          canvas.height = targetH;

          const ctx = canvas.getContext('2d');
          if (!ctx) { cleanup(); readOriginal(); return; }
          ctx.drawImage(img, 0, 0, targetW, targetH);

          const dataUrl = canvas.toDataURL('image/jpeg', QUALITY);
          const b64 = dataUrl.split(',')[1];
          cleanup();

          if (!b64) { readOriginal(); return; }

          // Guard against the pathological case where re-encoding grew the
          // file (can happen with already-optimized sources).
          const approxBytes = Math.floor(b64.length * 0.75);
          if (approxBytes >= file.size) { readOriginal(); return; }

          resolve({ base64: b64, name: file.name, compressed: true });
        } catch (err) {
          cleanup();
          readOriginal();
        }
      };

      img.onerror = () => { cleanup(); readOriginal(); };

      objectUrl = URL.createObjectURL(file);
      img.src = objectUrl;
    } catch (err) {
      cleanup();
      readOriginal();
    }
  });
}
